<template>
  <rv-step-sidebar-content
    data-automation="step-change-password-old__content"
    :title="$t('members.titles.changePassword')"
  >
    <form @submit.prevent.stop="">
      <rv-input
        v-model="password"
        autocomplete="old-password"
        bottom-slots
        class="q-mx-lg q-mb-lg"
        data-automation="step-change-password-old__old-password"
        :info-text="$t('members.titles.currentPasswordInfo')"
        :label="$t('members.titles.currentPassword')"
        :show-valid="false"
        :type="showPassword ? 'text' : 'password'"
        :validation="v$.password"
        @update:model-value="resetError"
      >
        <template #append>
          <q-icon
            v-show="!!password"
            class="cursor-pointer"
            :name="showPassword ? 'visibility' : 'visibility_off'"
            @click="showPassword = !showPassword"
          />
        </template>
      </rv-input>
    </form>

    <template #actions>
      <rv-button
        class="full-width"
        color="primary"
        data-automation="step-change-password-old__cancel"
        :label="$t('general.actions.cancel')"
        secondary
        @click="onBack"
      />

      <rv-button
        class="full-width"
        color="primary"
        data-automation="step-change-password-old__next"
        :disable="!password.length || loading"
        :label="$t('general.actions.next')"
        :loading="loading"
        @click="onNext"
      />
    </template>
  </rv-step-sidebar-content>
</template>

<script lang="ts" setup>
import {
  useStepSidebar,
  ValidationBuilder,
} from '@robovision/rvai-base'
import useVuelidate from '@vuelidate/core'
import type { PropType } from 'vue'
import {
  computed,
  ref,
} from 'vue'
import { useI18n } from 'vue-i18n'
import type { Member } from '~domain/Member'
import { useMembersStore } from '~stores/useMembersStore'
import { EditMemberSidebarSteps } from './EditMemberSidebarSteps'
import StepChangePasswordNew from './StepChangePasswordNew.vue'


const props = defineProps({
  member: {
    required: true,
    type: Object as PropType<Member>,
  },
})


const { checkPassword } = useMembersStore()
const {
  onBack,
  pushStep,
} = useStepSidebar()

const { t } = useI18n()


const loading = ref(false)
const password = ref('')
const showPassword = ref(false)
const passwordError = ref('')


const validationRules = computed(() => ({
  password: ValidationBuilder
    .$t('login.inputs.password')
    .required
    .customError(passwordError.value)
    .rules,
}))
const v$ = useVuelidate(validationRules, { password }, { $autoDirty: true })

async function onNext () {
  loading.value = true
  try {
    const isPasswordCorrect = await checkPassword(password.value)

    if (isPasswordCorrect) {
      pushStep({
        component: StepChangePasswordNew,
        componentProps: {
          currentPassword: password.value,
          member: props.member,
        },
        stepName: EditMemberSidebarSteps.ChangePasswordNew,
      })
    } else {
      passwordError.value = t('members.titles.incorrectPassword')
    }
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function resetError () {
  passwordError.value = ''
}

</script>
